import { FormEvent, useState } from 'react';
import { Sparkles, Mail, Loader2, CheckCircle2 } from 'lucide-react';
import { requestMagicLink } from '@/api/auth';

/**
 * Login is a single field — email. Server sends the magic link. We
 * always show the "if an account exists, we sent a link" stock message
 * (matches the server's anti-enumeration response).
 */
export function LoginScreen() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await requestMagicLink(email.trim());
      setSent(true);
    } catch (err) {
      setError((err as Error).message || 'Something went wrong, try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-nurev-bg px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-gradient shadow-brand-glow">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-nurev-text">
            NUREV Inference Console
          </h1>
          <p className="mt-2 text-sm text-nurev-muted">
            Sign in or get started — enter your email and we'll send a
            magic link.
          </p>
        </div>

        <div className="card">
          {sent ? (
            <div className="text-center">
              <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-nurev-purple-light" />
              <h2 className="text-lg font-semibold text-nurev-text">
                Check your inbox
              </h2>
              <p className="mt-2 text-sm text-nurev-text-soft">
                We sent a sign-in link to{' '}
                <span className="font-mono text-nurev-purple-light">
                  {email}
                </span>
                . Click it and you'll land in the console ready to
                mint your first API key.
              </p>
              <p className="mt-1 text-xs text-nurev-muted">
                The link is single-use and expires in 15 minutes.
              </p>
              <button
                className="btn-secondary mt-6 w-full"
                onClick={() => {
                  setSent(false);
                  setEmail('');
                }}
              >
                Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label className="label" htmlFor="email">
                  Email
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-nurev-muted" />
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    className="input pl-10"
                    placeholder="you@company.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                  />
                </div>
              </div>

              {error && (
                <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={submitting || !email}
                className="btn-primary w-full"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending link…
                  </>
                ) : (
                  'Send magic link →'
                )}
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-nurev-muted">
          Self-serve for individuals and small teams. Need procurement
          docs, security review, or volume pricing?{' '}
          <a
            href="mailto:sales@nurev.io"
            className="text-nurev-purple-light hover:underline"
          >
            sales@nurev.io
          </a>
          .
        </p>
      </div>
    </div>
  );
}
